import React from 'react';
import { IconButton, IconButtonProps, Tooltip } from '@mui/material';
import { useWriteGuard } from '../../context/useWriteGuard';

/**
 * Write-action icon button — the per-affordance read-only gate for icon-only
 * controls (typically row Delete / Remove, or an in-place Edit toggle).
 *
 * On a read-only env it renders DISABLED with a tooltip; otherwise a plain MUI
 * <IconButton> with every prop forwarded. See WriteButton for the
 * mutation-vs-navigation rule — use this ONLY for true mutations. A row "Edit"
 * pencil that NAVIGATES to a (read-only) detail page should stay a plain
 * <IconButton>.
 *
 * If the icon button already has its own descriptive Tooltip in the editable
 * state, prefer passing that copy through here is unnecessary — when read-only
 * this wrapper supplies the "switch env" tooltip; when editable it renders the
 * bare IconButton and the caller's surrounding Tooltip (if any) still applies.
 *
 * Pass `ignoreEnvReadOnly` for platform-DATA mutations (Consumer 360 / Member
 * Import) that are legitimately allowed in any env.
 */
type WriteIconButtonProps = IconButtonProps & { ignoreEnvReadOnly?: boolean };

const WriteIconButton = React.forwardRef<HTMLButtonElement, WriteIconButtonProps>(
  function WriteIconButton({ disabled, ignoreEnvReadOnly, ...rest }, ref) {
    const { readOnly: guardReadOnly, disabledProps } = useWriteGuard();
    const readOnly = ignoreEnvReadOnly ? false : guardReadOnly;
    const iconButton = (
      <IconButton ref={ref} data-write-action={ignoreEnvReadOnly ? undefined : 'true'} {...rest} disabled={disabled || readOnly} />
    );
    if (!readOnly) return iconButton;
    return (
      <Tooltip title={disabledProps.title ?? 'Read-only environment'} arrow>
        <span style={{ display: 'inline-flex' }}>{iconButton}</span>
      </Tooltip>
    );
  },
);

export default WriteIconButton;
